import React from 'react'
import { cn } from '../lib/utils.ts'
import InfoTip from './InfoTip.tsx'

interface StatCardProps {
  label: string
  value: string | number
  icon?: React.ReactNode
  subtitle?: string
  trend?: {
    value: number
    label?: string
  }
  /** Force green/red trend coloring even on gradient cards */
  trendColored?: boolean
  className?: string
  /** Gradient variant for hero KPI cards */
  gradient?: GradientVariant
  /** Plain-English explanation shown via an info icon next to the label */
  helpText?: string
}

const gradientMap = {
  green: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
  blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
  purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
  orange: 'bg-gradient-to-br from-orange-500 to-orange-600',
} as const

type GradientVariant = keyof typeof gradientMap

export default function StatCard({
  label,
  value,
  icon,
  subtitle,
  trend,
  trendColored = false,
  className = '',
  gradient,
  helpText,
}: StatCardProps) {
  const isGradient = !!gradient

  return (
    <div
      className={cn(
        'rounded-xl p-6 transition-all duration-200',
        isGradient
          ? `${gradientMap[gradient]} text-white shadow-lg`
          : 'bg-white border border-gray-200 shadow-card hover:shadow-card-hover hover:-translate-y-0.5',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-sm font-medium flex items-center gap-1',
              isGradient ? 'text-white/80' : 'text-gray-600',
            )}
          >
            <span className="min-w-0 truncate">{label}</span>
            {helpText && <InfoTip text={helpText} />}
          </p>
          <p className={cn('text-2xl font-bold mt-1', isGradient ? 'text-white' : 'text-gray-900')}>
            {value}
          </p>
          {subtitle && (
            <p className={cn('text-xs mt-1', isGradient ? 'text-white/70' : 'text-gray-500')}>
              {subtitle}
            </p>
          )}
          {trend && (
            <div className="mt-2">
              <span
                className={cn(
                  'text-sm font-medium',
                  isGradient && !trendColored
                    ? 'text-white/90'
                    : trend.value > 0
                      ? isGradient
                        ? 'text-green-200'
                        : 'text-green-600'
                      : trend.value < 0
                        ? isGradient
                          ? 'text-red-200'
                          : 'text-red-600'
                        : isGradient
                          ? 'text-white/70'
                          : 'text-gray-500',
                )}
              >
                {trend.value > 0 ? '↑' : trend.value < 0 ? '↓' : '–'} {Math.abs(trend.value)}%
                {trend.label && (
                  <span
                    className={cn('font-normal', isGradient ? 'text-white/60' : 'text-gray-500')}
                  >
                    {' '}
                    {trend.label}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        {icon && <div className={cn('ml-4 flex-shrink-0', isGradient && 'opacity-80')}>{icon}</div>}
      </div>
    </div>
  )
}
